import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowRight, Loader2 } from "lucide-react";
import { formatMoney } from "./planConfig";

function labelFromLookup(lookup) {
  return (lookup || "").replace("_", " · ");
}

function PreviewLoading() {
  return (
    <div className="py-10 flex items-center justify-center text-stone-500" data-testid="preview-loading">
      <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Calculating proration…
    </div>
  );
}

function ProrationLines({ lines, currency }) {
  if (!lines?.length) return null;
  return (
    <div className="text-xs text-stone-500 space-y-1 max-h-32 overflow-y-auto" data-testid="preview-lines">
      {lines.map((l, i) => (
        <div key={`${l.description || "line"}-${i}-${l.amount}`} className="flex justify-between gap-2">
          <span className="truncate">{l.description || (l.proration ? "Proration" : "Charge")}</span>
          <span className="font-mono">{formatMoney(l.amount, currency)}</span>
        </div>
      ))}
    </div>
  );
}

function PreviewSummary({ data }) {
  const interval = data.new_lookup_key?.endsWith("annual") ? " / year" : " / month";
  return (
    <div className="space-y-4" data-testid="preview-content">
      <div className="flex items-center justify-between text-sm">
        <span className="text-stone-500">Current plan</span>
        <span className="font-medium capitalize">{labelFromLookup(data.current_lookup_key)}</span>
      </div>
      <div className="flex items-center justify-center py-1"><ArrowRight className="w-4 h-4 text-stone-400" /></div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-stone-500">New plan</span>
        <span className="font-medium capitalize">{labelFromLookup(data.new_lookup_key)}</span>
      </div>
      <div className="p-4 rounded-xl bg-muted space-y-2">
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-stone-600">Due today</span>
          <span className="font-display text-2xl" data-testid="preview-amount-due">
            {formatMoney(data.amount_due, data.currency)}
          </span>
        </div>
        {data.proration_amount !== 0 && (
          <div className="text-xs text-stone-500" data-testid="preview-proration">
            Includes prorated adjustment of {formatMoney(data.proration_amount, data.currency)}
          </div>
        )}
        <div className="text-xs text-stone-500">
          Then {formatMoney(data.next_charge_amount, data.currency)}{interval}
        </div>
      </div>
      <ProrationLines lines={data.lines} currency={data.currency} />
    </div>
  );
}

export default function PreviewDialog({ preview, confirming, onConfirm, onClose }) {
  const isOpen = !!preview;
  const isLoading = preview?.loading || !preview?.data;

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-2xl max-w-md" data-testid="preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Switch plan</DialogTitle>
        </DialogHeader>
        {isLoading ? <PreviewLoading /> : <PreviewSummary data={preview.data} />}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-full" data-testid="preview-cancel-btn">Cancel</Button>
          <Button
            onClick={onConfirm}
            disabled={confirming || isLoading}
            className="rounded-full bg-primary text-primary-foreground"
            data-testid="preview-confirm-btn"
          >
            {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Confirm switch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
